import React, { Component } from 'react';
import {
  Flex,
  MenuItem,
  Text,
  Button,
  DropdownMenu,
  Menu,
  Dialog,
  DialogFooter,
  ConfirmDialog,
  Icon
} from 'core/components';
import styled from 'styled-components';
import { InputGroup } from 'core/form';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { fromNow } from 'core/util/dateUtils';

const KaizenMenuItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  li {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const KaizenMenuItem = styled(MenuItem)`
  li {
    width: 100%;
  }

  &.bp4-menu-item {
    flex: 1;
    padding: ${({ isCopilotMode }) => (isCopilotMode ? '6px 12px' : '12px 16px')};
    align-items: center;
    position: relative;
    min-height: ${({ isCopilotMode }) => (isCopilotMode ? 'auto' : '50px')};

    &::before,
    & > * {
      margin: 0;
    }
  }

  &.selected {
    .actions {
      background-color: ${(props) => (props.theme.name === 'light' ? '#dfe1e5' : '#4f5256')};
    }
  }
`;
@inject('$journeys')
@withRouter
@observer
class KaizenListItem extends Component {
  state = {
    journeyTitle: '',
    isEditing: false,
    showRenameDialog: false,
    showDeleteDialog: false,
    renameTitle: '',
    renamingLoading: false
  };

  constructor(props) {
    super(props);
    this.state.journeyTitle = this.getDisplayTitle(props.journey);
  }

  getDisplayTitle(journey) {
    return journey?.get('user_title') || journey?.get('session_title') || 'AI Advisor';
  }

  handleRenameClick = () => {
    const { journey } = this.props;
    this.setState({
      showRenameDialog: true,
      renameTitle: this.getDisplayTitle(journey)
    });
  };

  handleRenameTitleChange = (e) => {
    this.setState({ renameTitle: e.target.value });
  };

  handleRenameConfirm = async () => {
    const { journey } = this.props;
    const { renameTitle } = this.state;

    this.setState({ renamingLoading: true });

    try {
      await journey.save({ user_title: renameTitle }, { patch: true });
      this.setState({ showRenameDialog: false, renameTitle: '', renamingLoading: false });
    } catch (error) {
      console.error('Error renaming chat session:', error);
      this.setState({ renamingLoading: false });
      // Could add error handling/toast here
    }
  };

  handleDeleteConfirm = async () => {
    const { journey, history, $journeys } = this.props;

    try {
      await journey.destroy();
      this.setState({ showDeleteDialog: false });
      history.push($journeys.baseUrl);
    } catch (error) {
      console.error('Error deleting chat session:', error);
    }
  };

  handleTogglePin = async () => {
    const { journey } = this.props;
    const currentPinnedState = journey?.get('pinned');

    try {
      await journey.save({ pinned: !currentPinnedState });
    } catch (error) {
      console.error('Error toggling pin status:', error);
      // Could add error handling/toast here
    }
  };

  handleRenameCancel = () => {
    this.setState({ showRenameDialog: false, renameTitle: '', renamingLoading: false });
  };

  handleDeleteClick = () => {
    this.setState({ showDeleteDialog: true });
  };

  handleDeleteCancel = () => {
    this.setState({ showDeleteDialog: false });
  };

  renderChatDropdownMenu = () => {
    const { journey } = this.props;

    // only show pins for user chats
    const isPinned = journey?.get('pinned') && journey.isUserChat;

    if (!journey.isUserChat) {
      return null;
    }

    return (
      <DropdownMenu
        content={
          <Menu>
            {journey.isUserChat && (
              <MenuItem
                icon={isPinned ? 'unpin' : 'pin'}
                text={isPinned ? 'Unpin' : 'Pin'}
                onClick={this.handleTogglePin}
              />
            )}
            <MenuItem icon="edit" text="Rename" onClick={this.handleRenameClick} />
            <MenuItem icon="trash" intent="danger" text="Delete" onClick={this.handleDeleteClick} />
          </Menu>
        }
      >
        <Button icon="more" minimal small color="muted" style={{ flexShrink: 0 }} />
      </DropdownMenu>
    );
  };

  render() {
    const { journey, hideUserName, hideEditedDate, onSelectJourney, isCopilotMode } = this.props;
    const { isEditing, showRenameDialog, showDeleteDialog, renameTitle, renamingLoading } = this.state;
    const isPinned = journey?.get('pinned') && journey.isUserChat;

    if (isCopilotMode) {
      return (
        <>
          <KaizenMenuItemWrapper>
            <KaizenMenuItem
              isCopilotMode={isCopilotMode}
              text={
                <Flex alignItems="center" gap={1} justifyContent="space-between" width="100%">
                  <Flex alignItems="center" gap={1}>
                    {isPinned && <Icon icon="pin" color="muted" />}
                    {this.getDisplayTitle(journey)}
                    <Text muted small>
                      {fromNow(journey?.get('updated_at'))}
                    </Text>
                  </Flex>
                </Flex>
              }
              onClick={() => {
                onSelectJourney(journey);
              }}
              active={journey.isSelected}
            />
            {this.renderChatDropdownMenu()}
          </KaizenMenuItemWrapper>

          {/* Rename Dialog */}
          <Dialog
            title="Rename Conversation"
            isOpen={showRenameDialog}
            onClose={this.handleRenameCancel}
            style={{ width: 400 }}
          >
            <div style={{ padding: '16px' }}>
              <InputGroup
                value={renameTitle}
                onChange={this.handleRenameTitleChange}
                placeholder="Enter new title..."
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button text="Cancel" onClick={this.handleRenameCancel} mr={1} />
              <Button
                text="Rename"
                intent="primary"
                onClick={this.handleRenameConfirm}
                disabled={!renameTitle.trim() || renamingLoading}
                loading={renamingLoading}
              />
            </DialogFooter>
          </Dialog>

          <ConfirmDialog
            title="Delete Conversation"
            text={`Are you sure you want to delete ${this.getDisplayTitle(journey)}?`}
            isOpen={showDeleteDialog}
            onConfirm={this.handleDeleteConfirm}
            onCancel={this.handleDeleteCancel}
            confirmButtonText="Delete"
            confirmButtonIntent="danger"
          />
        </>
      );
    }

    return (
      <>
        <KaizenMenuItemWrapper>
          <KaizenMenuItem
            className={classNames({ editing: isEditing, selected: journey.isSelected })}
            text={
              <Flex flexDirection="column">
                <Flex alignItems="center" gap={1}>
                  {isPinned && <Icon icon="pin" style={{ flexShrink: 0 }} />}
                  <Text fontWeight={journey.isSelected ? 'bold' : 'normal'} ellipsis>
                    {this.getDisplayTitle(journey)}
                  </Text>
                </Flex>
                {!hideEditedDate && (
                  <Text muted small>
                    Edited {fromNow(journey?.get('updated_at'))} ago
                  </Text>
                )}
                {!hideUserName && (
                  <Text muted small>
                    {journey?.get('user.user_full_name') || journey?.get('user.user_email')}
                  </Text>
                )}
              </Flex>
            }
            active={journey.isSelected}
            onClick={() => {
              onSelectJourney(journey);
            }}
          />
          {this.renderChatDropdownMenu()}
        </KaizenMenuItemWrapper>

        <Dialog
          title="Rename Conversation"
          isOpen={showRenameDialog}
          onClose={this.handleRenameCancel}
          style={{ width: 400 }}
        >
          <div style={{ padding: '16px' }}>
            <InputGroup
              value={renameTitle}
              onChange={this.handleRenameTitleChange}
              placeholder="Enter new title..."
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button text="Cancel" onClick={this.handleRenameCancel} mr={1} />
            <Button
              text="Rename"
              intent="primary"
              onClick={this.handleRenameConfirm}
              disabled={!renameTitle.trim() || renamingLoading}
              loading={renamingLoading}
            />
          </DialogFooter>
        </Dialog>

        <ConfirmDialog
          title="Delete Conversation"
          model={journey}
          text={`Are you sure you want to delete ${this.getDisplayTitle(journey)}?`}
          isOpen={showDeleteDialog}
          onConfirm={this.handleDeleteConfirm}
          onCancel={this.handleDeleteCancel}
          confirmButtonText="Delete"
          confirmButtonIntent="danger"
        />
      </>
    );
  }
}

export default KaizenListItem;
